/**
 * New room (backlog #54): a name, people picked one by one, from the staff list, or in bulk (a department or group,
 * a building, a position, or everyone on a shift), then create it and start a voice or video call straight away.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ListChecks, Phone, Search, UsersRound, Video, X } from 'lucide-react';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { today } from '@/lib/format';
import { useLocations, useMembers, usePositions, useTeams, useWorkspaceId } from '@/lib/hooks';
import { qk, queryClient } from '@/lib/queryClient';
import { useUi } from '@/lib/store';
import { Avatar, Button, ErrorBox, Field, Input, Modal, Select, Toggle } from '@/components/ui';
import { PeoplePicker } from '@/features/people/PeoplePicker';
import { StaffListPicker } from '@/features/people/StaffListPicker';
import { useChatLimits } from '@/features/settings/PrefsPanels';
import { useStartCall } from '@/features/calls/useStartCall';
import { defaultRoomName, expandSource, mergeMembers, type BulkSource } from './roomMembers';
import { RoomIcon } from './RoomIcon';
import { useIsPhone } from '@/mobile/layout';
import { BottomSheet } from '@/mobile/BottomSheet';
import { useLongPress } from '@/mobile/longPress';
import { Spinner } from '@/components/ui';

/**
 * t226u: the phone's one sticky row: a wide yellow Create, and a square phone button that creates the room and starts
 * a voice call. Holding the phone button (or right-click) offers the video call instead.
 */
function PhoneCreateRow({ ready, busy, onCreate }: { ready: boolean; busy: null | 'room' | 'audio' | 'video'; onCreate: (m: 'room' | 'audio' | 'video') => void }) {
  const [menu, setMenu] = useState(false);
  const press = useLongPress(() => { if (ready && !busy) setMenu(true); }, ready && !busy);
  const off = !ready || Boolean(busy);
  return (
    <div className="relative flex items-center gap-2" data-testid="new-room-actions">
      <button type="button" disabled={off} onClick={() => onCreate('room')} data-testid="new-room-create"
        className="flex h-12 min-w-0 flex-1 items-center justify-center gap-2 rounded-xl bg-accent text-base font-semibold text-on-accent ring-1 ring-inset ring-black/10 active:bg-accent-pressed disabled:bg-slate-200 disabled:text-slate-500 disabled:ring-0 dark:disabled:bg-slate-800 dark:disabled:text-slate-400">
        {busy === 'room' && <Spinner className="h-4 w-4" />}Create
      </button>
      <button type="button" disabled={off} onClick={() => onCreate('audio')} {...press} aria-label="Create and start call" aria-haspopup="menu" aria-expanded={menu} data-testid="new-room-call"
        className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-slate-300 text-slate-900 active:bg-slate-100 disabled:opacity-40 dark:border-slate-600 dark:text-white dark:active:bg-slate-800">
        {busy === 'audio' || busy === 'video' ? <Spinner className="h-5 w-5" /> : <Phone className="h-5 w-5" aria-hidden />}
      </button>
      {menu && (
        <div role="menu" aria-label="Start a call" data-testid="new-room-call-menu" className="absolute bottom-full right-0 z-10 mb-2 w-56 overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-slate-700">
          <button type="button" role="menuitem" onClick={() => { setMenu(false); onCreate('audio'); }} className="flex h-12 w-full items-center gap-3 px-4 text-left text-[15px] active:bg-slate-100 dark:active:bg-slate-700"><Phone className="h-5 w-5" aria-hidden />Voice call</button>
          <button type="button" role="menuitem" onClick={() => { setMenu(false); onCreate('video'); }} data-testid="new-room-call-video" className="flex h-12 w-full items-center gap-3 border-t border-slate-100 px-4 text-left text-[15px] active:bg-slate-100 dark:border-slate-700 dark:active:bg-slate-700"><Video className="h-5 w-5" aria-hidden />Video call</button>
        </div>
      )}
    </div>
  );
}


export function NewRoomModal() {
  const open = useUi(s => s.newRoomOpen);
  const setOpen = useUi(s => s.setNewRoomOpen);
  return open ? <NewRoomDialog onClose={() => setOpen(false)} /> : null;
}

function NewRoomDialog({ onClose }: { onClose: () => void }) {
  const ws = useWorkspaceId();
  const { userId } = useAuth();
  const nav = useNavigate();
  const toast = useUi(s => s.toast);
  const startCall = useStartCall();
  const phone = useIsPhone();
  const { byId } = useMembers();
  const limit = useChatLimits()?.room_member_limit ?? 100;
  const [name, setName] = useState(() => defaultRoomName());
  const [members, setMembers] = useState<string[]>([]);
  const [membersCanAdd, setMembersCanAdd] = useState(true);
  const [staffList, setStaffList] = useState(false);
  const [bulk, setBulk] = useState('');
  const [shiftDate, setShiftDate] = useState(() => today());
  const [shiftKey, setShiftKey] = useState('');
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState<null | 'room' | 'audio' | 'video'>(null);
  const [error, setError] = useState<unknown>(null);
  const staff = useQuery({ queryKey: ['staff-directory', ws], queryFn: () => api.staffDirectory(ws!), enabled: Boolean(ws), staleTime: 60_000 });
  const shifts = useQuery({ queryKey: ['room-shift-groups', ws, shiftDate], queryFn: () => api.roomShiftGroups(ws!, shiftDate), enabled: Boolean(ws && shiftDate) });
  const { teams } = useTeams();
  const locations = (useLocations().data?.locations ?? []).filter(l => !l.archived_at);
  const positions = (usePositions().data?.positions ?? []).filter(p => !p.archived_at);
  const departments = teams.filter(t => t.kind === 'department' && !t.archived_at);
  const groups = teams.filter(t => t.kind === 'group' && !t.archived_at);
  const total = members.length + 1;     // including you
  const over = Math.max(0, total - limit);
  const room = Math.max(0, limit - total);

  const add = (ids: string[], what?: string) => {
    const r = mergeMembers(members, ids, userId);
    setMembers(r.members);
    toast(r.added ? `Added ${r.added} ${r.added === 1 ? 'person' : 'people'}${what ? ` from ${what}` : ''}` : `Everyone${what ? ` in ${what}` : ''} is already in the ad-hoc meeting room`, r.added ? 'success' : 'info');
  };
  const addBulk = (value: string) => {
    setBulk('');
    const [type, id] = value.split(':') as [BulkSource['type'], string];
    if (!type || !id || !staff.data) return;
    const label = type === 'team' ? teams.find(t => t.id === id)?.name : type === 'location' ? locations.find(l => l.id === id)?.name : positions.find(p => p.id === id)?.name;
    add(expandSource(staff.data.staff, { type, id }), label);
  };
  const addShift = () => {
    const g = shifts.data?.groups.find(x => x.key === shiftKey);
    if (g) add(g.user_ids, g.label);
  };
  useEffect(() => { setShiftKey(''); }, [shiftDate]);

  const shown = useMemo(() => {
    const t = filter.trim().toLowerCase();
    return members.map(id => ({ id, name: byId.get(id)?.display_name ?? 'Unknown' })).filter(m => !t || m.name.toLowerCase().includes(t));
  }, [members, byId, filter]);

  const create = async (mode: 'room' | 'audio' | 'video') => {
    if (!ws) return;
    setBusy(mode); setError(null);
    try {
      const c = await api.createRoom(ws, { name: name.trim(), user_ids: members, members_can_add: membersCanAdd });
      await queryClient.invalidateQueries({ queryKey: qk.conversations(ws) });
      onClose();
      nav(`/c/${c.id}`);
      if (mode !== 'room') void startCall({ conversation_id: c.id, media: mode });
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(null); }
  };
  const ready = Boolean(name.trim()) && members.length > 0 && over === 0;
  // t226u: the phone gets a compact sheet: name, people search with chips, one sticky Create row.
  if (phone) return (
    <BottomSheet open onClose={onClose} title="New ad-hoc meeting room" testId="new-room-sheet"
      footer={<PhoneCreateRow ready={ready} busy={busy} onCreate={create} />}>
      <div className="space-y-4 px-4 pb-3" data-testid="new-room-modal">
        <Field label="Ad-hoc meeting room name"><Input className="text-base" value={name} maxLength={80} onChange={e => setName(e.target.value)} aria-label="Ad-hoc meeting room name" /></Field>
        <div>
          <PeoplePicker searchOnly selected={members} max={Math.max(0, limit - 1)} onChange={ids => setMembers(ids.filter(x => x !== userId))} exclude={userId ? [userId] : []} placeholder="Search people" label="Add people" />
          {over > 0 && <p className="mt-1 text-xs font-medium text-red-700 dark:text-red-400" role="status">{over} over the limit of {limit}: remove some people</p>}
        </div>
        <Toggle checked={membersCanAdd} onChange={setMembersCanAdd} label="Members can add people" />
        <ErrorBox error={error} />
      </div>
    </BottomSheet>
  );

  return (
    <Modal open onClose={onClose} wide="xl" title={<span className="flex items-center gap-2"><RoomIcon className="h-5 w-5" />New ad-hoc meeting room</span>}
      footer={<div className="flex w-full flex-wrap items-center justify-end gap-2" data-testid="new-room-actions">
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button variant="secondary" disabled={!ready || Boolean(busy)} loading={busy === 'room'} onClick={() => void create('room')}>Create ad-hoc meeting room</Button>
        <Button variant="secondary" disabled={!ready || Boolean(busy)} loading={busy === 'audio'} onClick={() => void create('audio')}><Phone className="h-4 w-4" />Create and start voice call</Button>
        <Button disabled={!ready || Boolean(busy)} loading={busy === 'video'} onClick={() => void create('video')}><Video className="h-4 w-4" />Create and start video call</Button>
      </div>}>
      <div className="grid gap-5 md:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]" data-testid="new-room-modal">
      <div className="min-w-0 space-y-4">
        <p className="text-sm text-slate-600 dark:text-slate-400">An ad-hoc meeting room is a chat and call for people from any team. When you're done, close it: it leaves everyone's chat list and the history is kept.</p>
        <Field label="Ad-hoc meeting room name"><Input value={name} maxLength={80} onChange={e => setName(e.target.value)} aria-label="Ad-hoc meeting room name" /></Field>

        <Field label="Add people">
          <PeoplePicker selected={[]} onChange={ids => add(ids)} exclude={[...(userId ? [userId] : []), ...members]} placeholder="Search by name" />
        </Field>

        <div className="rounded-lg border border-slate-200 p-3 dark:border-slate-700" data-testid="room-bulk-add">
          <div className="mb-2 text-sm font-semibold">Add a whole group</div>
          <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
            <Select aria-label="Add a department, group, building or position" value={bulk} disabled={!staff.data} onChange={e => addBulk(e.target.value)} data-testid="room-bulk-select">
              <option value="">Department, group, building or position…</option>
              {departments.length > 0 && <optgroup label="Departments">{departments.map(t => <option key={t.id} value={`team:${t.id}`}>{t.name}</option>)}</optgroup>}
              {groups.length > 0 && <optgroup label="Groups">{groups.map(t => <option key={t.id} value={`team:${t.id}`}>{t.name}</option>)}</optgroup>}
              {locations.length > 0 && <optgroup label="Buildings">{locations.map(l => <option key={l.id} value={`location:${l.id}`}>{l.name}</option>)}</optgroup>}
              {positions.length > 0 && <optgroup label="Positions">{positions.map(p => <option key={p.id} value={`position:${p.id}`}>{p.name}</option>)}</optgroup>}
            </Select>
            <Button variant="secondary" onClick={() => setStaffList(true)} data-testid="room-staff-list"><ListChecks className="h-4 w-4" />Add from staff list</Button>
          </div>
          {shifts.data?.allowed !== false && (
            <div className="mt-3 space-y-2 border-t border-slate-100 pt-3 dark:border-slate-800" data-testid="room-shift-add">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-slate-600 dark:text-slate-400">People on shift on</span>
                <Input type="date" className="w-44" value={shiftDate} onChange={e => setShiftDate(e.target.value)} aria-label="Shift date" />
              </div>
              <div className="flex items-center gap-2">
                <Select className="min-w-0 flex-1" value={shiftKey} onChange={e => setShiftKey(e.target.value)} aria-label="Shift" title={shifts.data?.groups.find(g => g.key === shiftKey)?.label}>
                  <option value="">{shifts.data?.groups.length ? 'Pick a shift…' : 'No published shifts that day'}</option>
                  {shifts.data?.groups.map(g => <option key={g.key} value={g.key}>{g.label} ({g.count} {g.count === 1 ? 'person' : 'people'})</option>)}
                </Select>
                <Button size="sm" variant="secondary" disabled={!shiftKey} onClick={addShift}>Add</Button>
              </div>
            </div>
          )}
        </div>

      </div>
      <div className="min-w-0 space-y-3 md:border-l md:border-slate-200 md:pl-5 dark:md:border-slate-700">
        <div data-testid="room-members">
          <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm font-semibold"><UsersRound className="h-4 w-4" />
              <span data-testid="room-member-count">{total} {total === 1 ? 'person' : 'people'} including you</span>
              <span className={over ? 'font-medium text-red-700 dark:text-red-400' : 'font-normal text-slate-500'}>· {over ? `${over} over the limit of ${limit}: remove some people` : `up to ${limit}`}</span>
            </div>
            {members.length > 0 && <button type="button" className="text-xs text-slate-600 underline hover:text-slate-900 dark:text-slate-400" onClick={() => setMembers([])}>Remove everyone</button>}
          </div>
          {members.length > 8 && (
            <label className="mb-2 flex items-center gap-2 rounded-md border border-slate-300 px-2 py-1 dark:border-slate-700"><Search className="h-3.5 w-3.5 text-slate-500" />
              <input className="flex-1 bg-transparent text-sm outline-none" placeholder="Find someone in the ad-hoc meeting room" value={filter} onChange={e => setFilter(e.target.value)} aria-label="Find someone in the ad-hoc meeting room" /></label>
          )}
          {members.length === 0 ? <p className="rounded-md border border-dashed border-slate-300 p-3 text-sm text-slate-500 dark:border-slate-700">No one added yet.</p> : (
            <ul className="scroll-thin max-h-72 divide-y md:max-h-[26rem] divide-slate-100 overflow-y-auto rounded-md border border-slate-200 dark:divide-slate-800 dark:border-slate-700">
              {shown.map(m => (
                <li key={m.id} className="flex items-center gap-2 px-2 py-1.5 text-sm">
                  <Avatar size={22} name={m.name} id={m.id} /><span className="flex-1 truncate">{m.name}</span>
                  <button type="button" className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:hover:bg-slate-800" aria-label={`Remove ${m.name}`} title="Remove" onClick={() => setMembers(members.filter(x => x !== m.id))}><X className="h-3.5 w-3.5" /></button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <Toggle checked={membersCanAdd} onChange={setMembersCanAdd} label="Members can add people" />
        <p className="text-xs text-slate-500">You can rename the ad-hoc meeting room, take people out and close it at any time.</p>
        <ErrorBox error={error} />
      </div>
      </div>
      {staffList && (
        <Modal open wide="xl" onClose={() => setStaffList(false)} title="Add to the ad-hoc meeting room from the staff list">
          <StaffListPicker inIds={[...(userId ? [userId] : []), ...members]} notInLabel="Not in the ad-hoc meeting room" max={room} onAdd={ids => { add(ids); setStaffList(false); }} />
        </Modal>
      )}
    </Modal>
  );
}
